import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Cookie, Settings, Shield, X } from "lucide-react";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Delay showing banner for better UX
      setTimeout(() => setShowBanner(true), 1500);
    }
  }, []);

  const savePreferences = (acceptAll = false) => {
    const finalPreferences = acceptAll 
      ? { essential: true, analytics: true, marketing: true }
      : preferences;
    
    localStorage.setItem("cookie_consent", JSON.stringify({
      ...finalPreferences,
      timestamp: new Date().toISOString()
    }));
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const minimalPreferences = { essential: true, analytics: false, marketing: false };
    localStorage.setItem("cookie_consent", JSON.stringify({
      ...minimalPreferences,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Nous respectons votre vie privée</h3>
                <p className="text-sm text-muted-foreground max-w-xl">
                  Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. 
                  Vous pouvez choisir les cookies que vous acceptez.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowSettings(true)}
                data-testid="cookie-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Personnaliser
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
              >
                Refuser
              </Button>
              <Button 
                size="sm"
                onClick={() => savePreferences(true)}
                data-testid="cookie-accept-btn"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-lg border-0 shadow-2xl animate-in zoom-in-95 duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Préférences des cookies</h2>
                    <p className="text-sm text-muted-foreground">Gérez vos préférences de confidentialité</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowSettings(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <Label className="font-medium text-foreground">Cookies essentiels</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Nécessaires au fonctionnement du site (authentification, sécurité)
                    </p>
                  </div>
                  <Switch checked={true} disabled className="ml-4" />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <Label className="font-medium text-foreground">Cookies analytiques</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Nous aident à comprendre comment vous utilisez le site pour l'améliorer
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                    className="ml-4"
                    data-testid="cookie-analytics-switch"
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <Label className="font-medium text-foreground">Cookies marketing</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Permettent de personnaliser les publicités et mesurer leur efficacité
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                    className="ml-4"
                    data-testid="cookie-marketing-switch"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={rejectAll}
                >
                  Refuser tout
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => savePreferences(false)}
                  data-testid="cookie-save-btn"
                >
                  Enregistrer mes choix
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Consultez notre <a href="/confidentialite" className="text-primary hover:underline">politique de confidentialité</a> pour plus d'informations.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

// Export a function to open cookie settings from anywhere (e.g., footer link)
export function openCookieSettings() {
  localStorage.removeItem("cookie_consent");
  window.location.reload();
}
